import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Criteria } from '../components/AudienceCriteria/Criteria';
import { AudienceFields, type AudienceForm } from '../types';
import { hasExpiredSegments } from '../utils/hasExpiredSegments';

const styles = {
  leftColumn: {
    pt: 4,
    pb: 4,
    pl: 4,
  },
  rightColumn: {
    pt: 4,
    pb: 4,
    pl: 2,
  },
  title: {
    mb: 1,
    ':before': {
      borderTopColor: 'primary.main',
      borderTopStyle: 'solid',
      borderTopWidth: '1px ',
      content: '""',
      display: 'block',
      marginBottom: '8px',
      width: '12px',
    },
  },
} satisfies Record<string, SxProps<Theme>>;

export type AudienceCriteriaProps = {
  hideCriteria: boolean;
  showExcludeSection: boolean;
  setShowExcludeSection: Dispatch<SetStateAction<boolean>>;
  showDivider: boolean;
  isEdit: boolean;
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
};

export const AudienceCriteria = ({
  hideCriteria,
  setShowExcludeSection,
  showExcludeSection,
  showDivider,
  isEdit,
  isCreate,
  isCreateFromTemplate,
}: AudienceCriteriaProps) => {
  const { control } = useFormContext<AudienceForm>();
  const { t } = useTranslation();

  const observableExportType = useWatch({
    name: AudienceFields.ExportType,
    control,
  });

  const alertMessage = useMemo(() => {
    if (!observableExportType) {
      return t('Select Audience Options first to begin building your audience.');
    }

    return t('Select Match Type and Provider first.');
  }, [observableExportType, t]);

  const observableNormalizedDefinition = useWatch({
    control: control,
    name: 'normalized_definition',
  });

  const { hasExpired, hasSoonToExpire } = hasExpiredSegments(observableNormalizedDefinition);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Grid container>
        <Grid size={3} sx={styles.leftColumn}>
          <Box>
            <Typography
              variant="h2"
              data-test="audience-criteria-title"
              sx={[
                {
                  mb: 1,
                },
                styles.title,
              ]}
            >
              <Box
                component="span"
                sx={{
                  display: 'flex',
                }}
              >
                {t('Audience Criteria')}
              </Box>
            </Typography>
          </Box>
        </Grid>

        <Grid size={8} sx={styles.rightColumn}>
          {hasExpired && (
            <Alert severity="error" sx={{ mb: 2 }} data-test="expired-alert">
              {t(
                'Audience expired - deals using this audience may no longer be delivering. Review segments marked with an icon and replace or remove the expired ones to reactivate it.'
              )}
            </Alert>
          )}
          {hasSoonToExpire && (
            <Alert severity="warning" sx={{ mb: 2 }} data-test="expiring-soon-alert">
              {t(
                'Some segments in this audience will expire soon. Expired segments make the entire audience inactive - which may cause your deal to stop delivering. Review segments marked with an icon to avoid delivery issues.'
              )}
            </Alert>
          )}
          {hideCriteria ? (
            <Alert aria-label="Select Audience Options first to begin building your audience." severity="info">
              {alertMessage}
            </Alert>
          ) : (
            <Criteria
              isEdit={isEdit}
              isCreate={isCreate}
              isCreateFromTemplate={isCreateFromTemplate}
              showExcludeSection={showExcludeSection}
              setShowExcludeSection={setShowExcludeSection}
            />
          )}
        </Grid>
      </Grid>

      {showDivider && <Divider />}
    </Box>
  );
};
